/**
 * Swiss Enterprise Register API
 *
 * This module provides integration with the Swiss Federal Statistical Office's
 * Enterprise Register (UID Register) web service. The Enterprise Register is an
 * online database of all registered companies in Switzerland.
 *
 * Documentation: https://www.bfs.admin.ch/bfs/en/home/registers/enterprise-register/enterprise-identification/uid-register/uid-interfaces.html
 * Service: PublicServices via SOAP API
 * Endpoint: https://www.uid-wse.admin.ch/V5.0/PublicServices.svc
 */

import {
  SWISS_UID_REGISTER_API_ENDPOINT,
  SWISS_UID_REGISTER_API_TIMEOUT,
} from '../utils/constants';
import {
  SwissEnterpriseRegisterApiErrorCode,
  UIDApiResponse,
} from './swissEnterpriseRegisterApi.types';
import { formatUIDForAPI } from './swissEnterpriseRegisterApi.utils';

export class SwissEnterpriseRegisterApiError extends Error {
  public code: SwissEnterpriseRegisterApiErrorCode;

  public constructor(message: string, code: SwissEnterpriseRegisterApiErrorCode) {
    super(message);
    this.name = 'SwissEnterpriseRegisterApiError';
    this.code = code;
  }
}

// Namespace URIs
const NS_ECH_0097 = 'http://www.ech.ch/xmlns/eCH-0097/5';
const NS_ECH_0098 = 'http://www.ech.ch/xmlns/eCH-0098/5';

/**
 * Gets the first element by tag name and namespace, or null if not found
 */
export const getFirstElementByNS = (
  parent: Document | Element,
  namespace: string,
  tagName: string,
): Element | null => {
  const elements = parent.getElementsByTagNameNS(namespace, tagName);
  if (elements.length > 0) {
    return elements[0];
  }
  return null;
};

export const LEGAL_FORMS = {
  '0101': 'Sole proprietorship',
  '0103': 'General partnership',
  '0104': 'Limited partnership',
  '0105': 'Corporation with unlimited partners',
  '0106': 'Corporation (Ltd / AG)',
  '0107': 'Limited liability company (LLC / GmbH)',
  '0108': 'Cooperative',
  '0109': 'Association',
  '0110': 'Foundation',
  '0111': 'Branch of a foreign company',
  '0113': 'Special legal form',
  '0114': 'Limited Partnership for collective investment schemes (LPCI)',
  '0115': 'Investment company with variable capital (SICAV)',
  '0116': 'Investment company with fixed capital (SICAF)',
  '0117': 'Public-law institution',
  '0118': 'Non-commercial power of attorney',
  '0119': 'Representative of the community of property',
  '0151': 'Branch',
  '0220': 'Administrative unit of the Confederation',
  '0221': 'Administrative unit of the canton',
  '0222': 'Administrative unit of the district',
  '0223': 'Administrative unit of the commune',
  '0224': 'Other public-law administrative unit',
  '0230': 'Public-law institution of the Confederation',
  '0231': 'Public-law institution of the canton',
  '0232': 'Public-law institution of the district',
  '0233': 'Public-law institution of the commune',
  '0234': 'Other public-law institution',
  '0302': 'Simple partnership',
  '0312': 'Permanent establishment or Swiss representation of a foreign company',
  '0327': 'Foreign public company',
  '0328': 'Foreign public administration',
  '0329': 'International organisation',
  '0355': 'Other cooperative',
  '0361': 'Trust',
  '0362': 'Fund',
  '0441': 'Foreign company',
} as const;

/**
 * Normalizes legal form code to 4-digit format with leading zero
 * All UID legal form codes are 4 digits starting with 0
 */
export const normalizeLegalFormCode = (code: string): string => {
  if (!code) {
    return code;
  }
  const trimmed = code.trim();
  if (trimmed.length === 4) {
    return trimmed;
  }
  return trimmed.padStart(4, '0');
};

/**
 * Maps legal form code to display name
 */
export const getLegalFormName = (code: string): string => {
  const normalized = normalizeLegalFormCode(code);
  return LEGAL_FORMS[normalized as keyof typeof LEGAL_FORMS] || code;
};

/**
 * Gets legal form options for select dropdown
 * Returns array of options in format: { value: code, label: "code - name" }
 */
type LegalFormOption = { value: string; label: string };

export const getLegalFormOptions = (): LegalFormOption[] => Object.entries(LEGAL_FORMS).map(([code, name]) => ({
  value: code,
  label: `${code} - ${name}`,
}));

/**
 * Extracts organisation data from XML document
 */
export const extractOrganisationData = (
  xmlDoc: Document,
): {
  organisationName: string;
  organisationLegalName: string;
  legalForm: string;
} => {
  const organisationNameEl = getFirstElementByNS(xmlDoc, NS_ECH_0097, 'organisationName');
  const organisationLegalNameEl = getFirstElementByNS(xmlDoc, NS_ECH_0097, 'organisationLegalName');
  const legalFormEl = getFirstElementByNS(xmlDoc, NS_ECH_0097, 'legalForm');

  const organisationName = organisationNameEl?.textContent || '';
  const organisationLegalName = organisationLegalNameEl?.textContent || '';
  const legalFormRaw = legalFormEl?.textContent || '';
  const legalForm = normalizeLegalFormCode(legalFormRaw);

  return {
    organisationName,
    organisationLegalName,
    legalForm,
  };
};

/**
 * Finds the legal address element in the XML document
 */
export const findLegalAddress = (xmlDoc: Document): Element => {
  const addressEls = xmlDoc.getElementsByTagNameNS(NS_ECH_0098, 'address');

  for (let i = 0; i < addressEls.length; i++) {
    const addressCategoryEls = addressEls[i].getElementsByTagNameNS(
      NS_ECH_0098,
      'addressCategory',
    );
    if (addressCategoryEls.length > 0 && addressCategoryEls[0].textContent === 'LEGAL') {
      return addressEls[i];
    }
  }

  throw new SwissEnterpriseRegisterApiError('Legal address not found in response', 'PARSE_ERROR');
};

/**
 * Extracts address data from legal address element
 */
export const extractAddressData = (
  legalAddress: Element,
): {
  street: string;
  houseNumber: string;
  town: string;
  swissZipCode: string;
} => {
  const streetEl = getFirstElementByNS(legalAddress, NS_ECH_0098, 'street');
  const houseNumberEl = getFirstElementByNS(legalAddress, NS_ECH_0098, 'houseNumber');
  const townEl = getFirstElementByNS(legalAddress, NS_ECH_0098, 'town');
  const swissZipCodeEl = getFirstElementByNS(legalAddress, NS_ECH_0098, 'swissZipCode');

  const street = streetEl?.textContent || '';
  const houseNumber = houseNumberEl?.textContent || '';
  const town = townEl?.textContent || '';
  const swissZipCode = swissZipCodeEl?.textContent || '';

  return {
    street,
    houseNumber,
    town,
    swissZipCode,
  };
};

/**
 * Parses SOAP XML response from Swiss Enterprise Register API
 */
export const parseUIDResponse = (xmlString: string): UIDApiResponse => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

  // Check for parsing errors
  const parserError = xmlDoc.querySelector('parsererror');
  if (parserError) {
    throw new SwissEnterpriseRegisterApiError('Failed to parse XML response', 'PARSE_ERROR');
  }

  // Check for UID not found (empty GetByUIDResult)
  const getByUIDResult = xmlDoc.querySelector('GetByUIDResult');
  if (!getByUIDResult || !getByUIDResult.querySelector('organisation')) {
    throw new SwissEnterpriseRegisterApiError('UID not found', 'NOT_FOUND');
  }

  const organisationData = extractOrganisationData(xmlDoc);
  const legalAddress = findLegalAddress(xmlDoc);
  const addressData = extractAddressData(legalAddress);

  return {
    ...organisationData,
    address: addressData,
  };
};

/**
 * Validates UID via Swiss Enterprise Register API
 */
export const validateUID = async (uid: string): Promise<UIDApiResponse> => {
  const formatted = formatUIDForAPI(uid);

  if (!formatted) {
    throw new SwissEnterpriseRegisterApiError('Invalid UID format', 'PARSE_ERROR');
  }

  // Build SOAP XML request
  const soapBody = `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/" xmlns:uid="http://www.uid.admin.ch/xmlns/uid-wse" xmlns:ech="http://www.ech.ch/xmlns/eCH-0097/5">
  <soap:Body>
    <uid:GetByUID>
      <uid:uid>
        <ech:uidOrganisationIdCategorie>${formatted.category}</ech:uidOrganisationIdCategorie>
        <ech:uidOrganisationId>${formatted.id}</ech:uidOrganisationId>
      </uid:uid>
    </uid:GetByUID>
  </soap:Body>
</soap:Envelope>`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SWISS_UID_REGISTER_API_TIMEOUT);

  try {
    const response = await fetch(SWISS_UID_REGISTER_API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/xml; charset=utf-8',
        SOAPAction: 'http://www.uid.admin.ch/xmlns/uid-wse/IPublicServices/GetByUID',
      },
      body: soapBody,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const xmlText = await response.text();

    if (!response.ok) {
      const isNotFound = xmlText.includes('Data_validation_failed');
      throw new SwissEnterpriseRegisterApiError(
        `API request failed with status ${response.status}`,
        isNotFound ? 'NOT_FOUND' : 'NETWORK_ERROR',
      );
    }


    return parseUIDResponse(xmlText);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof SwissEnterpriseRegisterApiError) {
      throw error;
    }
    throw new SwissEnterpriseRegisterApiError('Unknown error occurred', 'NETWORK_ERROR');
  }
};
